'use client';

import { Box, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ReactNode } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import {
  ComponentBlocksTwoColumnTextImageBlock,
  Enum_Componentblockstwocolumntextimageblock_Imagealignment,
  Enum_Componentblockstwocolumntextimageblock_Imagecolumnsize,
  Enum_Componentblockstwocolumntextimageblock_Itemspacing,
  Enum_Componentblockstwocolumntextimageblock_Textcolumnsize,
} from '@/types';

import { StrapiImage } from '../../Utility/StrapiImage';

interface Props {
  block: ComponentBlocksTwoColumnTextImageBlock | null;
}

const getColumnSize = (size: Enum_Componentblockstwocolumntextimageblock_Textcolumnsize) => {
  switch (size) {
    case Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Percent25:
      return 25;
    case Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Percent30:
      return 30;
    case Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Percent33:
      return 33;
    case Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Percent35:
      return 35;
    case Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Percent40:
      return 40;
    case Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Percent45:
      return 45;
    case Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Percent55:
      return 55;
    case Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Percent60:
      return 69;
    case Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Percent65:
      return 65;
    case Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Percent70:
      return 70;
    case Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Percent75:
      return 75;
    default:
      return 50;
  }
};
const smallerAmount = (a: number, b: number) => (a <= b ? a : b);
const getColumnSizes = (
  size: Enum_Componentblockstwocolumntextimageblock_Imagecolumnsize,
  textColumnSize: Enum_Componentblockstwocolumntextimageblock_Textcolumnsize | undefined
) => {
  const image = getColumnSize(
    size as unknown as Enum_Componentblockstwocolumntextimageblock_Textcolumnsize
  );
  return {
    image: `${image}%`,
    text: `${
      textColumnSize
        ? smallerAmount(
            95 - image,
            getColumnSize(
              textColumnSize as unknown as Enum_Componentblockstwocolumntextimageblock_Textcolumnsize
            )
          )
        : 95 - image
    }%`,
  };
};

export function TwoColumnTextImageContent({ block }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!block) return null;

  const {
    ImageColRight,
    ImageAlignment,
    ImageColumnSize,
    Image,
    TwoColumnBody,
    ImageBorder,
    RoundImageCorners,
    ItemSpacing,
    TextColumnSize,
  } = block;

  const { image, text } = getColumnSizes(
    ImageColumnSize ?? Enum_Componentblockstwocolumntextimageblock_Imagecolumnsize.Percent35,
    TextColumnSize === Enum_Componentblockstwocolumntextimageblock_Textcolumnsize.Auto
      ? undefined
      : (TextColumnSize ?? undefined)
  );

  const align =
    ImageAlignment === Enum_Componentblockstwocolumntextimageblock_Imagealignment.Top
      ? 'flex-start'
      : 'center';

  const justify =
    ItemSpacing === Enum_Componentblockstwocolumntextimageblock_Itemspacing.Around
      ? 'space-around'
      : 'space-between';

  const renderTextBlock = (
    <Box
      sx={{
        maxWidth: { xs: '100%', md: text },
        flexBasis: { xs: '100%', md: text },
        textAlign: 'left',
      }}
    >
      {TwoColumnBody && Array.isArray(TwoColumnBody) && TwoColumnBody.length > 0 ? (
        <GlobalBlockRenderer
          content={TwoColumnBody}
          overrides={{
            blocks: {
              link: ({ url, children }: { url: string; children: ReactNode }) => (
                <Link
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {children}
                </Link>
              ),
            },
          }}
        />
      ) : (
        <Typography variant="body1">No content available</Typography>
      )}
    </Box>
  );

  const renderImageBlock = Image ? (
    <Box
      sx={(theme) => ({
        maxWidth: {
          xs: `calc(calc(20% + ${image}) + ${theme.spacing(4)})`,
          sm: image,
        },
        marginInline: {
          xs: `auto`,
          sm: 'inherit',
        },
        flexBasis: { xs: '100%', sm: image },
        flexShrink: 0,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        mx: 'auto',
        '> *': {
          ...(RoundImageCorners
            ? {
                overflow: 'hidden',
                borderRadius: 50,
              }
            : {}),
          ...(ImageBorder
            ? {
                overflow: 'hidden',
                border: `0.375rem solid ${theme.palette.purple[400]}`,
                '> *': {
                  marginBottom: '-0.2rem',
                },
              }
            : {}),
        },
      })}
    >
      <StrapiImage
        image={Image}
        size="large"
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: '12px',
        }}
      />
    </Box>
  ) : null;

  return (
    <Box data-component="TwoColumnTextImageContent">
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {renderTextBlock}
          {renderImageBlock}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: ImageColRight ? 'row-reverse' : 'row',
            alignItems: align,
            justifyContent: justify,
            gap: '5%',
          }}
        >
          {renderImageBlock}
          {renderTextBlock}
        </Box>
      )}
    </Box>
  );
}
